<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  responses: {
    type: Array,
    default: () => [],
  },
  selectedId: {
    type: [Number, null],
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  searchQuery: {
    type: String,
    default: '',
  },
  activeFilter: {
    type: String,
    default: 'all',
  },
});

const emit = defineEmits(['select', 'use', 'toggle-favorite']);

const { t } = useI18n();
</script>

<template>
  <div
    class="flex flex-col w-80 min-w-[20rem] border-r border-n-weak overflow-y-auto"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-sm text-n-slate-10">
        {{ t('CANNED_RESPONSE_BROWSER.LOADING') }}
      </span>
    </div>
    <div v-else-if="!responses.length" class="px-4 py-8 text-center">
      <p class="text-sm text-n-slate-10">
        <template v-if="activeFilter === 'favorites'">
          {{ t('CANNED_RESPONSE_BROWSER.FAVORITES_EMPTY') }}
        </template>
        <template v-else-if="searchQuery">
          {{ t('CANNED_RESPONSE_BROWSER.NO_RESULTS') }}
        </template>
        <template v-else>
          {{ t('CANNED_RESPONSE_BROWSER.EMPTY') }}
        </template>
      </p>
    </div>
    <template v-else>
      <button
        v-for="response in responses"
        :key="response.id"
        class="group flex items-center gap-2 px-4 py-2.5 text-left cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 border-b border-n-weak last:border-b-0"
        :class="{
          'bg-n-alpha-2 dark:bg-n-solid-2': selectedId === response.id,
        }"
        @click="emit('select', response)"
        @dblclick="emit('use', response)"
      >
        <div class="flex flex-col gap-0.5 flex-1 min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate w-full">
            {{ response.short_code }}
          </span>
          <span class="text-xs text-n-slate-10 truncate w-full">
            {{ response.content }}
          </span>
        </div>
        <span
          class="shrink-0 i-lucide-star size-4 cursor-pointer transition-colors"
          :class="
            response.is_favorite
              ? 'text-kpt-amber'
              : 'text-n-slate-10 opacity-0 group-hover:opacity-100'
          "
          :style="response.is_favorite ? 'fill: currentColor' : ''"
          :title="
            response.is_favorite
              ? t('CANNED_RESPONSE_BROWSER.UNMARK_FAVORITE')
              : t('CANNED_RESPONSE_BROWSER.MARK_FAVORITE')
          "
          @click.stop="emit('toggle-favorite', response)"
        />
      </button>
    </template>
  </div>
</template>
